import {
  Combobox,
  Field,
  Spinner,
} from '@noorden-insurtech/alba-design-system';

type CnaeOption = {
  value: string;
  label: string;
};

type Props = {
  value: string;
  onChange: (value: string, label?: string) => void;
  selectedLabel?: string;
  options?: CnaeOption[];
  useCombobox?: boolean;
  search?: string;
  onSearchChange?: (value: string) => void;
  isLoading?: boolean;
  required?: boolean;
  error?: string;
  disabled?: boolean;
};

/**
 * Para CPF/CAEPF, CNAE usa seleção pelo catálogo InsureMO. Para CNPJ, o campo
 * continua no modo input desabilitado até a regra específica ser definida.
 */
export function CnaeField({
  value,
  onChange,
  selectedLabel,
  options,
  useCombobox,
  search = '',
  onSearchChange,
  isLoading,
  required,
  error,
  disabled,
}: Props) {
  if (useCombobox) {
    const rawItems = options ?? [];
    const hasSelectedItem = rawItems.some(item => item.value === value);
    const items =
      value && selectedLabel && !hasSelectedItem
        ? [{ value, label: selectedLabel }, ...rawItems]
        : rawItems;
    const selectedItem = items.find(item => item.value === value);
    const displayLabel = selectedItem?.label ?? selectedLabel ?? value;

    return (
      <Field.Root invalid={!!error}>
        <Field.Label className='text-sm font-bold text-alba-black-500'>
          CNAE
          {required && <span className='text-alba-error-red-500'>*</span>}
        </Field.Label>
        <Combobox.Root
          items={items}
          value={value ? [value] : []}
          onValueChange={details => {
            const selectedValue = details.value[0] || '';
            const selectedOption = items.find(
              item => item.value === selectedValue
            );
            onChange(selectedValue, selectedOption?.label);
          }}
          onInputValueChange={details => onSearchChange?.(details.inputValue)}
          selectionBehavior='replace'
          positioning={{ sameWidth: true }}
          disabled={disabled}
        >
          <Combobox.Control>
            <Combobox.Trigger className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-left text-base text-alba-gray-600 disabled:cursor-not-allowed disabled:bg-alba-gray-100'>
              {displayLabel || 'Selecione o CNAE'}
            </Combobox.Trigger>
          </Combobox.Control>
          <Combobox.Positioner>
            <Combobox.Content>
              <Combobox.Input placeholder='Buscar CNAE...' autoFocus />
              {isLoading ? (
                <div className='flex items-center justify-center py-4'>
                  <Spinner size='sm' colorScheme='blue' />
                </div>
              ) : (
                <Combobox.List>
                  {items.map(item => (
                    <Combobox.Item key={item.value} item={item}>
                      <Combobox.ItemText>{item.label}</Combobox.ItemText>
                      <Combobox.ItemIndicator />
                    </Combobox.Item>
                  ))}
                  {items.length === 0 && search.trim().length > 0 && (
                    <Combobox.Empty />
                  )}
                </Combobox.List>
              )}
            </Combobox.Content>
          </Combobox.Positioner>
        </Combobox.Root>
        {error && <Field.ErrorText>{error}</Field.ErrorText>}
      </Field.Root>
    );
  }

  return (
    <Field.Root invalid={!!error}>
      <Field.Label className='text-sm font-bold text-alba-black-500'>
        CNAE
        {required && <span className='text-alba-error-red-500'>*</span>}
      </Field.Label>
      <Field.Input
        value={selectedLabel || value}
        onChange={e => onChange(e.target.value)}
        placeholder='Digite o CNAE'
        disabled={disabled}
      />
      {error && <Field.ErrorText>{error}</Field.ErrorText>}
    </Field.Root>
  );
}
